import { useState } from 'react';
import { X } from 'lucide-react';
import './PendingOrdersBanner.scss';

interface PendingOrdersBannerProps {
  count: number;
  /** True when the table is already filtered to the loads this banner is about. */
  isReviewing: boolean;
  onReview: () => void;
}

/**
 * Points admins at loads waiting on an approve/reject decision.
 *
 * The sidebar badge says work exists from anywhere in the app; this is the other
 * half — the one click that gets you to it, filtered and ready for the bulk
 * approve toolbar.
 *
 * It hides itself once the table is already showing those loads, so it never
 * sits there telling you about something you are looking at. The manual dismiss
 * lasts for the page visit only; a banner you can permanently silence is a
 * banner that stops working.
 */
export function PendingOrdersBanner({ count, isReviewing, onReview }: PendingOrdersBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (count <= 0 || isReviewing || dismissed) return null;

  return (
    <div className="pending-banner" role="status">
      <span className="pending-banner__dot" aria-hidden="true" />

      <span className="pending-banner__text">
        <strong>
          {count} order{count === 1 ? '' : 's'}
        </strong>{' '}
        awaiting approval
      </span>

      <button type="button" className="btn btn--primary btn--sm" onClick={onReview}>
        Review
      </button>

      <button
        type="button"
        className="pending-banner__dismiss"
        onClick={() => setDismissed(true)}
        title="Hide until next visit"
        aria-label="Hide until next visit"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default PendingOrdersBanner;
